import type { Point } from '@os/engine';
import type { NewObject } from './commands';
import type { ConnectorEnd } from './schema';
import { SHAPES } from './schema';

export const FLOWCHART_TEMPLATES = ['process', 'decision'] as const;
export type FlowchartTemplate = (typeof FLOWCHART_TEMPLATES)[number];
export type FlowchartLabels = Record<'start' | 'step1' | 'step2' | 'end' | 'choice' | 'yes' | 'no', string>;

const W = 160;
const H = 80;
const GAP = 80;
type Node = { ref: string; shape: (typeof SHAPES)[number]; col: number; row: number; label: string };
type Edge = [from: string, fromSide: Side, to: string, toSide: Side];
type Side = 'top' | 'right' | 'bottom' | 'left';
const ANCHOR: Record<Side, { nx: number; ny: number }> = { top: { nx: 0.5, ny: 0 }, right: { nx: 1, ny: 0.5 }, bottom: { nx: 0.5, ny: 1 }, left: { nx: 0, ny: 0.5 } };

function layout(kind: FlowchartTemplate, l: FlowchartLabels): { nodes: Node[]; edges: Edge[] } {
  if (kind === 'process')
    return {
      nodes: [
        { ref: 'start', shape: 'ellipse', col: 0, row: 0, label: l.start },
        { ref: 'step1', shape: 'rectangle', col: 1, row: 0, label: l.step1 },
        { ref: 'step2', shape: 'rectangle', col: 2, row: 0, label: l.step2 },
        { ref: 'end', shape: 'ellipse', col: 3, row: 0, label: l.end },
      ],
      edges: [['start', 'right', 'step1', 'left'], ['step1', 'right', 'step2', 'left'], ['step2', 'right', 'end', 'left']],
    };
  return {
    nodes: [
      { ref: 'start', shape: 'ellipse', col: 0, row: 0, label: l.start },
      { ref: 'choice', shape: 'diamond', col: 1, row: 0, label: l.choice },
      { ref: 'yes', shape: 'rectangle', col: 2, row: 0, label: l.yes },
      { ref: 'no', shape: 'rectangle', col: 1, row: 1, label: l.no },
      { ref: 'end', shape: 'ellipse', col: 2, row: 1, label: l.end },
    ],
    edges: [
      ['start', 'right', 'choice', 'left'],
      ['choice', 'right', 'yes', 'left'],
      ['choice', 'bottom', 'no', 'top'],
      ['yes', 'bottom', 'end', 'top'],
      ['no', 'right', 'end', 'left'],
    ],
  };
}

/** A ready-made flowchart of labelled shapes and elbow connectors attached to their side midpoints, centred on `at`. */
export function flowchartTemplate(kind: FlowchartTemplate, at: Point, labels: FlowchartLabels): { ref: string; data: NewObject }[] {
  const { nodes, edges } = layout(kind, labels);
  const cols = Math.max(...nodes.map((n) => n.col)) + 1;
  const rows = Math.max(...nodes.map((n) => n.row)) + 1;
  const x0 = at.x - (cols * W + (cols - 1) * GAP) / 2;
  const y0 = at.y - (rows * H + (rows - 1) * GAP) / 2;
  const base = { parentId: null, rotation: 0, locked: false, style: {} };
  const pos = new Map(nodes.map((n) => [n.ref, { x: x0 + n.col * (W + GAP), y: y0 + n.row * (H + GAP) }]));
  const end = (ref: string, side: Side): ConnectorEnd => {
    const p = pos.get(ref)!;
    const a = ANCHOR[side];
    return { objectId: ref, anchor: a, x: p.x + a.nx * W, y: p.y + a.ny * H };
  };
  return [
    ...nodes.map((n) => ({ ref: n.ref, data: { ...base, ...pos.get(n.ref)!, w: W, h: H, type: 'shape', shape: n.shape, text: n.label } as NewObject })),
    ...edges.map(([a, sa, b, sb], i) => ({
      ref: `edge${i}`,
      data: { ...base, x: 0, y: 0, w: 1, h: 1, type: 'connector', start: end(a, sa), end: end(b, sb), routing: 'elbow', startCap: 'none', endCap: 'arrow' } as NewObject,
    })),
  ];
}
